import { Max } from "@pdfking/math"
import type { TrackingMessage, TrackingStatus } from "@pdfking/planner"
import { Init } from "@pdfking/reactivity/preact"
import { NullQ } from "@pdfking/structs"
import type { Time } from "@pdfking/structs"
import {
  GuidToString,
  Length,
  MethodDispatcher,
  Partial,
  RecordCreate,
  SafePromise,
  Sleep,
  UnixTime,
  UUID,
  Void,
} from "@pdfking/tfunc"

/** How a tracked promise reports progress — see `track_promise`. */
export type TrackEmit = (status: TrackingStatus) => void

type Counter = { done: number; total: number }
type Task = {
  error: unknown
  finished: boolean
  start_at: number
  status: Map<string, Map<string, Counter>>
}
// A group's live task count — kept alongside the tasks so "has every task
// feeding this group finished" is a lookup, not a pass over every task.
type Group = { task_done: number; task_total: number }

// The severity buckets a tracker splits its progress across.
const TRACKER_STATUSES = ["danger", "success", "loading"] as const
export type TrackerStatus = (typeof TRACKER_STATUSES)[number]
// A progress bar's running totals, accumulated over the task pass. `is_finished`
// is a live getter (kept as a getter only on `aggregated`, see `make_tracker`)
// so it reflects the mutations done during that pass.
export type TrackerBar = {
  done: number
  is_finished: boolean
  task_done: number
  task_total: number
  total: number
}
// A per-status slice also exposes its `width` (share of the tracker's total).
export type TrackerStatusBar = TrackerBar & { width: number }
// One named group's aggregated progress plus its danger/success/loading split —
// what `trackers` yields per key and TrackingToaster renders.
export type Tracker = {
  aggregated: TrackerBar
  status: Record<TrackerStatus, TrackerStatusBar>
  title: string
  width: number
}

// How long a finished tracker lingers in the store before it's dropped. Keeps
// the completed bars on screen for a beat after work ends; once the last one is
// cleared `is_loading` flips false and TrackingToaster dismisses the toast.
const CLEANUP_DELAY = 7 as Time
const CLEANUP_ERROR_DELAY = 15 as Time
// How often the bars are recomputed while messages stream in. A split or a
// rasterize run sends thousands of status messages; the bars only need to move
// four times a second.
const REFRESH_DELAY = 0.25 as Time

// Adapted from apps/frontend's state/tracking.ts (the reactive tracking store),
// kept on the @pdfking/reactivity abstraction so the state handle stays
// backend-agnostic. The dispatcher is exported as `on_message` and fed straight
// into CreateEngine, and each consumer owns its instance.
//
// The tasks themselves are not reactive: every message updates them in place,
// and only `tick` — bumped at most once per REFRESH_DELAY — tells `trackers` to
// re-aggregate. Cost per message is constant, whatever the number of tasks.
export const CreateTracking = () => {
  const tasks = new Map<string, Task>()
  const groups = new Map<string, Group>()
  let refresh_pending = false

  return Init({
    computed: {
      is_loading() {
        return Length(this.trackers) > 0
      },
      trackers(): Record<string, Tracker> {
        void this.tick
        const blank = (): TrackerBar => ({
          done: 0,
          get is_finished() {
            return this.task_total - this.task_done == 0
          },
          task_done: 0,
          task_total: 0,
          total: 0,
        })
        const trackers: Record<string, Tracker> = {}
        const make_tracker = (k: string): Tracker => ({
          aggregated: blank(),
          status: RecordCreate(
            TRACKER_STATUSES.map(
              status =>
                [
                  status,
                  {
                    ...blank(),
                    get width() {
                      return this.done / trackers[k].aggregated.total
                    },
                  } as TrackerStatusBar,
                ] as const
            )
          ),
          title: k,
          get width() {
            return 1 / Length(trackers)
          },
        })
        for (const task of tasks.values()) {
          const key: TrackerStatus = task.error ? "danger" : task.finished ? "success" : "loading"
          for (const [id, counters] of task.status) {
            for (const { done, total } of counters.values()) {
              const tracker = trackers[id] || (trackers[id] = make_tracker(id))
              for (const r of [tracker.status[key], tracker.aggregated]) {
                r.done += done
                r.total += total
                r.task_done += task.finished ? 1 : 0
                r.task_total += 1
              }
            }
          }
        }
        return trackers
      },
    },
    data: {
      tick: 0,
    },
    methods: {
      refresh() {
        if (refresh_pending) return
        refresh_pending = true
        void Sleep(REFRESH_DELAY).then(() => {
          refresh_pending = false
          this.tick++
        })
      },
      // Scoped to the groups the finished task belongs to. A split runs as
      // several batch tasks under one group ("SPLIT_PDF") finishing at
      // different times; each finish schedules its own cleanup, and only once
      // every task feeding a group has finished is that group dropped — from
      // every task at once, never a chunk at a time. Earlier cleanups find the
      // group still loading and return straight away.
      cleanup_tracker(task_id: string) {
        const to_delete = [...(tasks.get(task_id)?.status.keys() ?? [])].filter(name => {
          const group = groups.get(name)
          return !NullQ(group) && group.task_done === group.task_total
        })
        if (to_delete.length == 0) return
        for (const name of to_delete) groups.delete(name)
        for (const [id, task] of tasks) {
          for (const name of to_delete) task.status.delete(name)
          if (task.status.size == 0) tasks.delete(id)
        }
        this.refresh()
      },
      emit(
        task_id: string,
        progress_type: string,
        progress_id: string,
        {
          done = undefined,
          total = undefined,
        }: { done?: number | undefined; total?: number | undefined }
      ) {
        const task = this.start(task_id)
        let counters = task.status.get(progress_type)
        if (!counters) {
          counters = new Map()
          task.status.set(progress_type, counters)
        }
        const r = counters.get(progress_id)
        if (!r) {
          const group = groups.get(progress_type) ?? { task_done: 0, task_total: 0 }
          group.task_total += 1
          if (task.finished) group.task_done += 1
          groups.set(progress_type, group)
        }
        const new_done = done || r?.done || 0
        const new_total = Max(total || r?.total || 1, new_done)
        counters.set(progress_id, { done: new_done, total: new_total })
        this.refresh()

        if (new_done === new_total) this.finish(task_id)
      },
      finish(task_id: string, error?: unknown, timeout: number | undefined = undefined) {
        const task = this.start(task_id)
        for (const [name, counters] of task.status) {
          for (const [id, counter] of counters) {
            counters.set(id, { done: counter.total, total: counter.total })
          }
          if (!task.finished) groups.get(name)!.task_done += counters.size
        }
        task.finished = true
        task.error = error
        this.refresh()
        const delay = error ? CLEANUP_ERROR_DELAY : ((timeout as Time | undefined) ?? CLEANUP_DELAY)
        void Sleep(delay).then(Partial(this.cleanup_tracker, task_id))
      },
      start(task_id: string): Task {
        const known = tasks.get(task_id)
        if (known) return known
        const task: Task = {
          error: undefined,
          finished: false,
          start_at: UnixTime(),
          status: new Map(),
        }
        tasks.set(task_id, task)
        this.refresh()
        return task
      },
      on_message(msg: TrackingMessage) {
        MethodDispatcher<[TrackingMessage], void>({
          start: m => void this.start(m.guid),
          status: m => this.emit(m.guid, m.name, m.guid, m),
          stop: m => this.finish(m.guid, m.error),
        })(msg)
      },
    },
  })
}

/**
 * Run one promise as its own tracked task on `tracking`: start → the
 * provider's own progress emits → finish (carrying the error if it threw, so
 * the bar goes red before it lingers out). The emit handed to the provider
 * closes over the task id, so callers never touch `emit`'s raw
 * (task_id, progress_type, progress_id, counter) signature.
 */
export const TrackPromise = async <T>(
  tracking: ReturnType<typeof CreateTracking>,
  provider: (emit: TrackEmit) => Promise<T>,
  on_start: (emit: TrackEmit) => void = Void,
  on_finish: (emit: TrackEmit) => void = Void
): Promise<T> => {
  const task_id = GuidToString(UUID())
  const emit: TrackEmit = event => tracking.emit(task_id, event.name, task_id, event)
  tracking.start(task_id)
  void on_start(emit)
  const { value, error } = await SafePromise(provider(emit))
  if (error) {
    tracking.finish(task_id, error)
    throw error
  }
  void on_finish(emit)
  tracking.finish(task_id)
  return value
}
